import FormGroupRenderer from '@app/components/FormGroupRenderer'
import { ILeasePriceTerm, LeasePriceTerm } from '@app/models/LeasePriceTerm'
import { bindFunctions } from '@app/utils/class'
import * as React from 'react'
import {
  Button,
  ButtonProps,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
} from 'reactstrap'

interface ILeasePriceBreakdownEditorProps extends ButtonProps {
  children: React.ReactNode
  onSave: (term: LeasePriceTerm) => void
  term?: ILeasePriceTerm
}

interface ILeasePriceBreakdownEditorState {
  isOpen: boolean
  term?: LeasePriceTerm
}

export default class LeasePriceBreakdownEditor extends React.PureComponent<
  ILeasePriceBreakdownEditorProps,
  ILeasePriceBreakdownEditorState
> {
  private formRef: React.RefObject<HTMLFormElement>

  constructor(props: ILeasePriceBreakdownEditorProps) {
    super(props)
    bindFunctions(this)
    this.formRef = React.createRef()
    this.state = {
      isOpen: false,
    }
  }

  public render() {
    const { children, onClick, onSave, term, ...buttonProps } = this.props
    return (
      <Button {...buttonProps} onClick={this._onShowModal}>
        {children}
        {this._renderModal()}
      </Button>
    )
  }

  private _isValid(): boolean {
    return (
      this.state.term !== undefined &&
      this.formRef.current !== null &&
      this.formRef.current.checkValidity()
    )
  }

  private _onChangeCommissionRate(newValue: number) {
    if (this.state.term) {
      this.setState({
        term: this.state.term.set('comissionRate', newValue) as LeasePriceTerm,
      })
    }
  }

  private _onChangeFees(newValue: number) {
    if (this.state.term) {
      this.setState({
        term: this.state.term.set('fees', newValue) as LeasePriceTerm,
      })
    }
  }

  private _onChangeTerm(newValue: string) {
    if (this.state.term) {
      this.setState({
        term: this.state.term.set('term', newValue) as LeasePriceTerm,
      })
    }
  }

  private _onChangeTotalPerMonth(newValue: string) {
    if (this.state.term) {
      this.setState({
        term: this.state.term.set('totalPerMonth', newValue) as LeasePriceTerm,
      })
    }
  }

  private _onChangeTotalPerTerm(newValue: number) {
    if (this.state.term) {
      this.setState({
        term: this.state.term.set('totalPerTerm', newValue) as LeasePriceTerm,
      })
    }
  }

  private _onChangeTotalPerYear(newValue: number) {
    if (this.state.term) {
      this.setState({
        term: this.state.term.set('totalPerYear', newValue) as LeasePriceTerm,
      })
    }
  }

  private _onCloseModal() {
    this.setState({ isOpen: false, term: undefined })
  }

  private _onSave() {
    if (this.state.term) {
      this.props.onSave(this.state.term)
    }
    this._onCloseModal()
  }

  private _onShowModal() {
    this.setState({
      isOpen: true,
      term: this.props.term
        ? (this.props.term as LeasePriceTerm)
        : new LeasePriceTerm(),
    })
  }

  private _renderModal() {
    if (!this.state.term) {
      return null
    }

    return (
      <Modal
        centered
        isOpen={this.state.isOpen}
        size='lg'
        toggle={this._onCloseModal}
      >
        <ModalHeader toggle={this._onCloseModal}>
          {this.props.term
            ? `Editing Term: ${this.props.term.term}`
            : 'New Term'}
        </ModalHeader>
        <ModalBody>
          <form ref={this.formRef}>
            <FormGroupRenderer
              labelText='Term'
              name='Term'
              onChange={this._onChangeTerm}
              required
              value={this.state.term.term}
            />
            <FormGroupRenderer
              labelText='Total $ Per Month'
              name='TotalPerMonth'
              onChange={this._onChangeTotalPerMonth}
              required
              value={this.state.term.totalPerMonth}
            />
            <FormGroupRenderer
              labelText='Total $ Per Year'
              name='TotalPerYear'
              onChange={this._onChangeTotalPerYear}
              required
              step='0.01'
              type='number'
              value={this.state.term.totalPerYear}
            />
            <FormGroupRenderer
              labelText='Total $ Per Term'
              name='TotalPerTerm'
              onChange={this._onChangeTotalPerTerm}
              required
              step='0.01'
              type='number'
              value={this.state.term.totalPerTerm}
            />
            <FormGroupRenderer
              labelText='Commission Rate'
              name='CommissionRate'
              onChange={this._onChangeCommissionRate}
              required
              step='0.01'
              type='number'
              value={this.state.term.comissionRate}
            />
            <FormGroupRenderer
              labelText='Fees'
              name='Fees'
              onChange={this._onChangeFees}
              required
              step='0.01'
              type='number'
              value={this.state.term.fees}
            />
          </form>
        </ModalBody>
        <ModalFooter>
          <Button color='secondary' outline onClick={this._onCloseModal}>
            Cancel
          </Button>
          <Button
            color='primary'
            disabled={!this._isValid()}
            onClick={this._onSave}
          >
            OK
          </Button>
        </ModalFooter>
      </Modal>
    )
  }
}
